import type { CarmaImageFit } from '../Image.type';

/** Default ImageKit transformation applied to images sent via PAPI */
const IMAGEKIT_DEFAULT_TRANSFORM = '/tr:n-web';
const DEFAULT_IMAGE_QUALITY = 70;

type CarmaImageLoaderProps = {
  src: string;
  width: number;
  height?: number;
  quality?: number;
  fit?: CarmaImageFit;
};

type CarmaImageLoader = (props: CarmaImageLoaderProps) => string;

type CarmaGenericImageLoaderProps = {
  src: string;
  width: number;
  quality?: number;
};

/**
 * Image loader for Carma ImageKit CDN
 * Applies ImageKit transformations for optimized image delivery
 */
const imageLoaderCarma: CarmaImageLoader = ({ src, width, height, quality, fit }) => {
  const processedSrc = src.replaceAll(IMAGEKIT_DEFAULT_TRANSFORM, '');

  // No format optimisation for SVG
  if (src.endsWith('.svg')) {
    return processedSrc;
  }

  const params = ['f-auto', `w-${width}`, `q-${quality ?? DEFAULT_IMAGE_QUALITY}`];

  if (height) {
    params.push(`h-${height}`);
    // Only set the fit if height is defined
    if (fit === 'fit') {
      params.push('c-maintain_ratio');
    }
  }

  return `${processedSrc}?tr=${params.join(',')}`;
};

/**
 * Image loader for Contentful CDN
 * Applies Contentful image API transformations
 */
const imageLoaderContentful: CarmaImageLoader = ({ src, width, height, quality, fit }) => {
  // No format optimisation for SVG
  if (src.endsWith('.svg')) {
    return src;
  }

  const params = new URLSearchParams({
    w: `${width}`,
    q: `${quality ?? DEFAULT_IMAGE_QUALITY}`,
    fm: 'webp',
  });

  if (height) {
    params.set('h', `${height}`);
    // Only set the fit if height is defined
    if (fit === 'fit') {
      params.set('fit', 'pad');
    } else {
      params.set('fit', 'fill');
    }
  }

  return `${src}?${params.toString()}`;
};

/**
 * Default loader that returns the source URL unchanged
 * Used as fallback when no specific loader is found
 */
const defaultLoader: CarmaImageLoader = ({ src }) => src;

const IMAGE_LOADERS: Record<string, CarmaImageLoader> = {
  'img.carma.com.au': imageLoaderCarma,
  'images.ctfassets.net': imageLoaderContentful,
} as const;

const SUPPORTED_DOMAINS = Object.keys(IMAGE_LOADERS);

/**
 * Finds the matching domain key for a given image source URL
 * @param src - The image source URL
 * @returns The matching domain key, or undefined if no match found
 */
const findMatchingDomain = (src: string): string | undefined => {
  return SUPPORTED_DOMAINS.find((domain) => src.includes(domain));
};

/**
 * Encodes spaces in an image URL (e.g., replaces spaces with %20)
 * @param src - The image source URL
 * @returns The encoded URL
 */
export const encodeImageSrc = (src: string): string => {
  return src.replaceAll(' ', '%20');
};

/**
 * Checks if a specific image loader exists for the given source URL
 * @param src - The image source URL
 * @returns True if a loader exists, false otherwise
 */
export const hasImageLoader = (src: string = ''): boolean => {
  const matchingDomain = findMatchingDomain(src);
  return matchingDomain !== undefined && matchingDomain in IMAGE_LOADERS;
};

/**
 * Gets the appropriate image loader for a given source URL
 * @param src - The image source URL
 * @returns The matching image loader, or the default loader if no match found
 */
export const getCarmaGenericImageLoader = (src: string = ''): CarmaImageLoader => {
  const matchingDomain = findMatchingDomain(src);
  if (matchingDomain && matchingDomain in IMAGE_LOADERS) {
    return IMAGE_LOADERS[matchingDomain];
  }

  console.warn(`No image loader found for src: "${src}"`);
  return defaultLoader;
};

/**
 * Generic image loader for CDN image URLs.
 *
 * Automatically selects the appropriate loader based on the image source domain.
 * If no dedicated loader is found, it falls back to the default loader.
 *
 * @param props - Image loader properties
 * @param props.src - The image source URL
 * @param props.width - The desired image width
 * @param props.quality - Optional image quality (defaults to 70)
 * @returns The processed image URL
 */
export const carmaGenericImageLoader = ({ src, width, quality }: CarmaGenericImageLoaderProps): string => {
  const encodedSrc = encodeImageSrc(src);
  const loader = getCarmaGenericImageLoader(encodedSrc);

  return loader({ src: encodedSrc, width, quality });
};
